import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type CSSProperties, type JSX } from 'react';
import type { BookDto } from '../api/books.js';
import { ApiError, userErrorMessage } from '../api/errors.js';
import { monetizationApi, redirectToCheckout } from '../api/monetization.js';
import { videosApi, type VideoSceneDto } from '../api/videos.js';
import { Link } from 'react-router';
import { EmptyState } from '../components/EmptyState.js';
import { GenerationFailed } from '../components/GenerationFailed.js';
import { useLegal } from '../brand/legal-context.js';
import { useI18n } from '../i18n/context.js';
import { spreadAspect } from './artwork-aspect.js';
import { ReplacementPackModal } from './ReplacementPackModal.js';

export function VideoStep({
  book,
  billingExempt = false,
}: {
  book: BookDto;
  billingExempt?: boolean;
}): JSX.Element {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [replacementNeeded, setReplacementNeeded] = useState(false);
  const query = useQuery({
    queryKey: ['book-videos', book.id],
    queryFn: () => videosApi.list(book.id),
    refetchInterval: (current) => {
      const progress = current.state.data?.progress;
      return progress && progress.queued + progress.generating > 0 ? 3_000 : false;
    },
  });

  /*
   * The server finishes a step's work on the step's own endpoint (it syncs finished
   * generations as it answers), so the book the sidebar reads learns of it only when
   * refetched. Without this a finished step stayed "Empty" or "In progress" until reload.
   */
  const dataUpdatedAt = query.dataUpdatedAt;
  useEffect(() => {
    if (dataUpdatedAt) void queryClient.invalidateQueries({ queryKey: ['book', book.id] });
  }, [book.id, dataUpdatedAt, queryClient]);

  const refresh = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['book-videos', book.id] }),
      queryClient.invalidateQueries({ queryKey: ['book', book.id] }),
    ]);
  };

  const run = (key: string, action: Promise<unknown>): void => {
    setPending(key);
    setError(null);
    void action
      .then(refresh)
      .catch((reason: unknown) => {
        if (reason instanceof ApiError && reason.code === 'plan_limit_exceeded') {
          setReplacementNeeded(true);
          return;
        }
        setError(userErrorMessage(reason, t));
      })
      .finally(() => setPending(null));
  };
  const regenerate = (scene: VideoSceneDto): void => {
    if (
      !billingExempt &&
      !query.data?.replacements.unlimited &&
      scene.videoAssetId &&
      query.data?.replacements.remaining === 0
    ) {
      setReplacementNeeded(true);
      return;
    }
    run(scene.id, videosApi.regenerate(scene.id));
  };
  const legal = useLegal();
  const replacementPack = useMutation({
    mutationFn: () =>
      monetizationApi.buyImageReplacementPack(book.id, legal.documentVersionId, 'video'),
    onSuccess: (result) => redirectToCheckout(result),
  });

  if (query.isPending) return <p>{t.common.loading}</p>;
  if (query.isError) return <p role="alert">{query.error.message}</p>;

  const { progress, cost, replacements } = query.data;
  const enabled = book.attributes.video.enabled;
  const generationActive = progress.queued + progress.generating > 0;

  return (
    <div className="grid gap-6">
      <div>
        <h1 className="text-2xl font-semibold">{t.video.title}</h1>
        <p className="mt-1 text-sm text-[var(--kitup-color-muted)]">{t.video.subtitle}</p>
      </div>

      {/* Nothing left to animate: the offer to animate "0 spreads" goes with it. */}
      {cost.batchSceneCount > 0 || pending === 'all' ? (
        <div className="grid gap-2 rounded-[var(--kitup-radius-sm)] border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950">
          <p className="font-semibold">{t.video.batchWarningTitle}</p>
          <p>{t.video.batchWarning(cost.batchSceneCount)}</p>
          <button
            type="button"
            className="kitup-primary-button justify-self-start"
            disabled={
              !enabled || pending !== null || generationActive || cost.batchSceneCount === 0
            }
            onClick={() => run('all', videosApi.generateAll(book.id))}
          >
            {pending === 'all' ? t.video.startingBatch : t.video.startBatch}
          </button>
        </div>
      ) : null}

      <div role="status" className="text-sm text-[var(--kitup-color-muted)]">
        {t.video.progress(progress.ready, progress.total)}
        {progress.queued + progress.generating > 0
          ? ` · ${t.video.eta(progress.estimatedSeconds)}`
          : ''}
        {progress.waitingForImage > 0
          ? ` · ${t.video.waitingForImage(progress.waitingForImage)}`
          : ''}
        {` · ${
          replacements.unlimited || billingExempt
            ? t.admin.accounts.replacementUnlimited
            : t.monetization.replacementsLeft(
                replacements.remaining,
                replacements.included + replacements.extra,
              )
        }`}
      </div>
      {error ? (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      ) : null}
      {!enabled ? (
        <p className="text-sm text-[var(--kitup-color-muted)]">{t.video.disabled}</p>
      ) : null}
      {query.data.items.length === 0 ? (
        <EmptyState
          title={t.video.empty}
          action={
            <Link to="?step=story" className="kitup-secondary-button">
              {t.builder.goToStep(t.builder.steps.story)}
            </Link>
          }
        />
      ) : null}

      <ul className="grid gap-5 md:grid-cols-2">
        {query.data.items.map((scene) => (
          <li key={scene.id} className="kitup-card overflow-hidden">
            <VideoPreview scene={scene} aspect={spreadAspect(book.attributes, scene)} />
            <div className="grid gap-4 p-4">
              <div className="flex items-center justify-between gap-3">
                <h2 className="font-semibold">{t.story.manual.spread(scene.index + 1)}</h2>
                <span className={`kitup-status kitup-status-${scene.status}`}>
                  {t.video.statuses[scene.status]}
                </span>
              </div>
              {scene.lastError ? <GenerationFailed error={scene.lastError} /> : null}
              {/*
                Before anything is animated, "Animate all spreads" is the one way in: a
                "Create video" on every spread beside it was nine ways to start the same thing.
                Per spread, it comes back as Regenerate — or Try again after a failure.
              */}
              {scene.videoAssetId || scene.lastError || cost.batchSceneCount === 0 ? (
                <button
                  type="button"
                  className="kitup-secondary-button justify-self-start"
                  disabled={!enabled || !scene.imageAssetId || pending !== null || generationActive}
                  onClick={() => regenerate(scene)}
                >
                  {pending === scene.id
                    ? t.video.starting
                    : scene.videoAssetId
                      ? t.video.regenerate
                      : t.video.generate}
                </button>
              ) : null}
              {!scene.imageAssetId ? (
                <p className="text-sm text-amber-800">{t.video.imageRequired}</p>
              ) : null}
              {scene.variants.length > 1 ? (
                <div>
                  <p className="mb-2 text-sm font-medium">{t.video.variants}</p>
                  <div className="flex gap-2 overflow-x-auto">
                    {scene.variants.map((variant) => (
                      <button
                        key={variant.id}
                        type="button"
                        aria-pressed={scene.videoAssetId === variant.assetId}
                        aria-label={t.video.variant(scene.index + 1)}
                        className="shrink-0 rounded-lg border-2 p-1 aria-pressed:border-[var(--kitup-color-accent)]"
                        onClick={() =>
                          run(
                            `variant-${variant.id}`,
                            videosApi.selectVariant(scene.id, variant.id),
                          )
                        }
                      >
                        <video src={variant.url} muted className="h-20 w-28 rounded object-cover" />
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
      {replacementNeeded ? (
        <ReplacementPackModal
          pending={replacementPack.isPending}
          error={replacementPack.isError}
          onClose={() => setReplacementNeeded(false)}
          onPay={() => replacementPack.mutate()}
        />
      ) : null}
    </div>
  );
}

function VideoPreview({
  scene,
  aspect,
}: {
  scene: VideoSceneDto;
  aspect: CSSProperties;
}): JSX.Element {
  const { t } = useI18n();
  return scene.video ? (
    <video
      src={scene.video.url}
      poster={scene.image?.url}
      controls
      muted
      loop
      playsInline
      aria-label={t.video.preview(scene.index + 1)}
      className="w-full bg-slate-100 object-cover"
      style={aspect}
    />
  ) : scene.image ? (
    <img
      src={scene.image.url}
      alt={t.video.imagePreview(scene.index + 1)}
      className="w-full bg-slate-100 object-cover"
      style={aspect}
    />
  ) : (
    <div
      className="grid place-items-center bg-slate-100 text-sm text-[var(--kitup-color-muted)]"
      style={aspect}
    >
      {t.video.noPreview}
    </div>
  );
}
